import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Calendar,
  Building2,
  User,
  DollarSign,
  FileText,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertCircle,
} from 'lucide-react';
import { useState } from 'react';
import {
  type CheckTransaction,
  getCheckStatusDisplay,
  getExpirationStatus,
  getDaysUntilExpiration,
  getCheckExpirationDate,
} from '@/hooks/queries/useChecks';
import { DepositCheckModal } from './DepositCheckModal';
import { DishonorCheckModal } from './DishonorCheckModal';
import { CancelCheckModal } from './CancelCheckModal';
import { ClearCheckModal } from './ClearCheckModal';

interface CheckDetailModalProps {
  open: boolean;
  onClose: () => void;
  check: CheckTransaction | null;
}

export function CheckDetailModal({ open, onClose, check }: CheckDetailModalProps) {
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showDishonorModal, setShowDishonorModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);

  if (!check) return null;

  const statusDisplay = getCheckStatusDisplay(check.check_status);
  const expirationStatus = getExpirationStatus(check.check_date);
  const daysRemaining = getDaysUntilExpiration(check.check_date);
  const expirationDate = getCheckExpirationDate(check.check_date);
  const showExpiration = check.check_status === 'pending' || check.check_status === 'deposited';

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              Détails du chèque
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Status & Type Header */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                {check.type === 'income' ? (
                  <>
                    <div className="p-2 bg-green-100 rounded-lg">
                      <TrendingDown className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Chèque reçu</p>
                      <p className="text-sm text-gray-600">Encaissement client</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Chèque émis</p>
                      <p className="text-sm text-gray-600">Paiement fournisseur</p>
                    </div>
                  </>
                )}
              </div>
              <Badge className={`${statusDisplay.bgColor} ${statusDisplay.color} text-base px-4 py-2`}>
                {statusDisplay.icon} {statusDisplay.label}
              </Badge>
            </div>

            {/* Expiration Warning */}
            {showExpiration && daysRemaining <= 30 && (
              <div className={`p-4 rounded-lg border-2 ${
                daysRemaining < 0
                  ? 'bg-red-50 border-red-200'
                  : daysRemaining <= 7
                  ? 'bg-red-50 border-red-200'
                  : daysRemaining <= 30
                  ? 'bg-orange-50 border-orange-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 mt-0.5 ${
                    daysRemaining < 0
                      ? 'text-red-600'
                      : daysRemaining <= 7
                      ? 'text-red-600'
                      : 'text-orange-600'
                  }`} />
                  <div>
                    <p className={`font-semibold ${
                      daysRemaining < 0 ? 'text-red-900' : 'text-orange-900'
                    }`}>
                      {daysRemaining < 0
                        ? '⚠️ Chèque expiré!'
                        : `⚠️ ${daysRemaining} jour(s) restant(s)`
                      }
                    </p>
                    <p className="text-sm text-gray-700 mt-1">
                      {daysRemaining < 0
                        ? `Ce chèque a expiré le ${formatDate(expirationDate.toISOString())}. Les chèques sont valables 180 jours en Algérie.`
                        : `Ce chèque expire le ${formatDate(expirationDate.toISOString())}. Déposez-le rapidement!`
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Check Information */}
            <div className="grid grid-cols-2 gap-4">
              {/* Check Number */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Numéro de chèque
                </label>
                <p className="text-lg font-mono font-semibold text-gray-900">
                  {check.check_number}
                </p>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Montant
                </label>
                <p className={`text-2xl font-bold ${
                  check.type === 'income' ? 'text-green-600' : 'text-gray-900'
                }`}>
                  {formatCurrency(check.amount)}
                </p>
              </div>

              {/* Drawer/Beneficiary */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {check.type === 'income' ? 'Tireur' : 'Bénéficiaire'}
                </label>
                <p className="text-base font-medium text-gray-900">
                  {check.check_drawer_name}
                </p>
              </div>

              {/* Bank */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Banque
                </label>
                <p className="text-base text-gray-900">
                  {check.check_bank || '-'}
                </p>
              </div>

              {/* Check Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date du chèque
                </label>
                <p className="text-base text-gray-900">
                  {formatDate(check.check_date)}
                </p>
              </div>

              {/* Expiration Date */}
              {showExpiration && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Date d'expiration
                  </label>
                  <div>
                    <p className={`text-base font-medium ${expirationStatus.color}`}>
                      {formatDate(expirationDate.toISOString())}
                    </p>
                    <p className={`text-sm ${expirationStatus.color}`}>
                      {daysRemaining < 0 ? 'Expiré' : `${daysRemaining} jours restants`}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {check.description && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Description
                </label>
                <p className="text-base text-gray-900 p-3 bg-gray-50 rounded-lg">
                  {check.description}
                </p>
              </div>
            )}

            {/* Notes */}
            {check.notes && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Notes
                </label>
                <div className="text-sm text-gray-700 p-3 bg-gray-50 rounded-lg whitespace-pre-wrap">
                  {check.notes}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              {check.check_status === 'pending' && (
                <>
                  <Button
                    onClick={() => setShowDepositModal(true)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    🏦 Déposer le chèque
                  </Button>
                  <Button
                    onClick={() => setShowDishonorModal(true)}
                    variant="destructive"
                    className="flex-1"
                  >
                    ❌ Marquer comme rejeté
                  </Button>
                  <Button
                    onClick={() => setShowCancelModal(true)}
                    variant="outline"
                  >
                    ⛔ Annuler
                  </Button>
                </>
              )}

              {check.check_status === 'deposited' && (
                <>
                  <Button
                    onClick={() => setShowClearModal(true)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    ✅ Marquer comme encaissé
                  </Button>
                  <Button
                    onClick={() => setShowDishonorModal(true)}
                    variant="destructive"
                    className="flex-1"
                  >
                    ❌ Marquer comme rejeté
                  </Button>
                </>
              )}

              {(check.check_status === 'cleared' || check.check_status === 'bounced' || check.check_status === 'cancelled') && (
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="flex-1"
                >
                  Fermer
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Modals */}
      <DepositCheckModal
        open={showDepositModal}
        onClose={() => {
          setShowDepositModal(false);
          onClose();
        }}
        check={check}
      />

      <DishonorCheckModal
        open={showDishonorModal}
        onClose={() => {
          setShowDishonorModal(false);
          onClose();
        }}
        check={check}
      />

      <CancelCheckModal
        open={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          onClose();
        }}
        check={check}
      />

      <ClearCheckModal
        open={showClearModal}
        onClose={() => {
          setShowClearModal(false);
          onClose();
        }}
        check={check}
      />
    </>
  );
}
